import type { HttpAgent } from "@icp-sdk/core/agent";
import type { ProtocolActor } from "./agent";
import type { IcClientConfig } from "./config";
import { amountToNat, natToAmount } from "./candid-helpers";
import { approveIcrcSpender, approveUsdmForProtocol } from "./icrc";
import { unwrapResult } from "./result";

export type MmkSwapParamsUi = {
	capMmk: number;
	totalSoldMmk: number;
	enabled: boolean;
	mmkPerUsdmHint: number;
};

export async function getMmkSwapParams(
	actor: ProtocolActor,
): Promise<MmkSwapParamsUi> {
	const p = await actor.getMmkSwapParams();
	return {
		capMmk: natToAmount(p.capMmkE8),
		totalSoldMmk: natToAmount(p.totalSoldMmkE8),
		enabled: p.enabled,
		mmkPerUsdmHint: natToAmount(p.mmkPerUsdmE8),
	};
}

export async function quoteMmkSwap(
	actor: ProtocolActor,
	usdmIn: number,
): Promise<number | null> {
	const opt = await actor.quoteMmkSwap(amountToNat(usdmIn));
	if (!opt || opt.length === 0) return null;
	return natToAmount(opt[0]!);
}

export async function buyMmkWithUsdm(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	usdmIn: number,
): Promise<number> {
	const ledgers = await actor.getLedgerCanisters();
	await approveUsdmForProtocol(agent, config, ledgers, usdmIn);
	const out = unwrapResult<bigint>(
		await actor.buyMmkWithUsdm(amountToNat(usdmIn)),
		"buyMmkWithUsdm",
	);
	return natToAmount(out);
}

export type GasFundingStatusUi = {
	open: boolean;
	opsIcp: number;
	gasTargetIcp: number;
	gasFloorIcp: number;
	feeBps: number;
	discountBps: number;
	treasuryMmk: number;
	spotUsdmPerMmk: number;
};

export async function getGasFundingStatusUi(
	actor: ProtocolActor,
): Promise<GasFundingStatusUi> {
	const g = await actor.getGasFundingStatus();
	const discountBps =
		typeof (g as { discountBps?: bigint }).discountBps === "bigint"
			? Number((g as { discountBps: bigint }).discountBps)
			: 500;
	return {
		open: Boolean(g.open),
		opsIcp: natToAmount(g.opsIcpE8),
		gasTargetIcp: natToAmount(g.gasTargetIcpE8),
		gasFloorIcp: natToAmount(g.gasFloorIcpE8),
		feeBps: Number(g.feeBps),
		discountBps,
		treasuryMmk: natToAmount(g.treasuryMmkE8),
		spotUsdmPerMmk: natToAmount(g.spotUsdmPerMmkE8),
	};
}

export async function quoteBuyMmkWithIcp(
	actor: ProtocolActor,
	icpIn: number,
): Promise<number> {
	const a = actor as ProtocolActor & {
		quoteBuyMmkWithIcp?: (n: bigint) => Promise<bigint>;
	};
	if (typeof a.quoteBuyMmkWithIcp !== "function") return 0;
	const out = await a.quoteBuyMmkWithIcp(amountToNat(icpIn));
	return natToAmount(out);
}

export async function buyMmkWithIcp(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	icpIn: number,
): Promise<number> {
	const icpId = config.ledgers.icp;
	if (!icpId) throw new Error("ICP ledger not configured");
	await approveIcrcSpender(agent, icpId, config.protocolCanisterId, icpIn);
	const out = unwrapResult<bigint>(
		await actor.buyMmkWithIcp(amountToNat(icpIn)),
		"buyMmkWithIcp",
	);
	return natToAmount(out);
}

export async function getStakingBalance(actor: ProtocolActor): Promise<number> {
	return actor.getStakingBalance();
}

export async function stakeMmk(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	amount: number,
): Promise<void> {
	const ledgers = await actor.getLedgerCanisters();
	const mmkId =
		config.ledgers.mmk ??
		(ledgers.mmk.length > 0 ? ledgers.mmk[0]!.toText() : null);
	if (!mmkId) throw new Error("MMK ledger not configured");
	await approveIcrcSpender(agent, mmkId, config.protocolCanisterId, amount);
	const e8 = amountToNat(amount);
	// Prefer exact e8 API when candid is current; fall back to Float wrapper.
	const a = actor as ProtocolActor & {
		stakeMMKE8?: (n: bigint) => Promise<unknown>;
	};
	if (typeof a.stakeMMKE8 === "function") {
		unwrapResult(await a.stakeMMKE8(e8), "stakeMMKE8");
	} else {
		unwrapResult(await actor.stakeMMK(amount), "stakeMMK");
	}
}

export async function unstakeMmk(
	actor: ProtocolActor,
	amount: number,
): Promise<void> {
	const e8 = amountToNat(amount);
	const a = actor as ProtocolActor & {
		unstakeMMKE8?: (n: bigint) => Promise<unknown>;
	};
	if (typeof a.unstakeMMKE8 === "function") {
		unwrapResult(await a.unstakeMMKE8(e8), "unstakeMMKE8");
	} else {
		unwrapResult(await actor.unstakeMMK(amount), "unstakeMMK");
	}
}

export async function voteProposal(
	actor: ProtocolActor,
	proposalId: bigint,
	support: boolean,
): Promise<void> {
	unwrapResult(await actor.voteProposal(proposalId, support), "voteProposal");
}

export async function createTextProposal(
	actor: ProtocolActor,
	title: string,
	description: string,
): Promise<bigint> {
	return unwrapResult<bigint>(
		await actor.createProposal(title, description, { TextOnly: null }),
		"createProposal",
	);
}

export async function getSavingsBalance(actor: ProtocolActor): Promise<number> {
	return actor.getSavingsBalance();
}

export async function depositSavings(
	actor: ProtocolActor,
	agent: HttpAgent,
	config: IcClientConfig,
	amount: number,
): Promise<void> {
	const ledgers = await actor.getLedgerCanisters();
	await approveUsdmForProtocol(agent, config, ledgers, amount);
	unwrapResult(await actor.depositSavings(amount), "depositSavings");
}

export async function withdrawSavings(
	actor: ProtocolActor,
	amount: number,
): Promise<void> {
	unwrapResult(await actor.withdrawSavings(amount), "withdrawSavings");
}

export async function getSavingsRateApr(
	actor: ProtocolActor,
): Promise<number | null> {
	try {
		const p = await actor.getProtocolParams();
		return Number(p.savingsRateBps) / 100;
	} catch {
		return null;
	}
}

export type TokenomicsUi = {
	soldMmk: number;
	capMmk: number;
	remainingMint: number;
	spotUsdmPerMmk: number;
	multipleBps: number;
	treasuryMmk: number;
};

export async function getTokenomicsStatusUi(
	actor: ProtocolActor,
): Promise<TokenomicsUi> {
	const t = await actor.getTokenomicsStatus();
	return {
		soldMmk: natToAmount(t.soldMmkE8),
		capMmk: natToAmount(t.capMmkE8),
		remainingMint: natToAmount(t.remainingMintE8),
		spotUsdmPerMmk: natToAmount(t.spotUsdmPerMmkE8),
		multipleBps: Number(t.multipleBps),
		treasuryMmk: natToAmount(t.treasuryMmkE8),
	};
}
